import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { initPdf, drawHeader, drawFooter, drawPageNumbers, formatTime, safeFilename, PAGE } from './pdfTheme';
import type { Result } from '../../types/results';
import type { Competition, Category, Discipline } from '../../types/domain';

interface Args {
  competition: Competition;
  category: Category;
  discipline: Discipline;
  results: Result[];
}

/**
 * Vrati nejlepsi vysledek per team — analogicky bestAttemptPerTeam,
 * ale ale i pro discipliny s 2 pokusy se vraci 1 radek per team (best).
 */
function bestPerOwner(results: Result[]): Result[] {
  const map = new Map<string, Result>();
  for (const r of results) {
    const key = `t${r.teamId ?? 0}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, r);
      continue;
    }
    // Lepsi = ma platny rank a nizsi (ranky jsou jiz spoctene serverem)
    if (existing.rank == null && r.rank != null) {
      map.set(key, r);
    } else if (existing.rank != null && r.rank != null && r.rank < existing.rank) {
      map.set(key, r);
    } else if (!existing.isNp && r.timeFinalMs != null && existing.timeFinalMs != null && r.timeFinalMs < existing.timeFinalMs) {
      map.set(key, r);
    }
  }
  return Array.from(map.values());
}

/**
 * TYP A — pořadí v 1 disciplíně. Jeden řádek per team/závodník (best pokus).
 * Sloupce: Pořadí · Start.č. · Družstvo · Výsl. čas · Ukončen.
 */
export function generateDisciplineRanking({ competition, category, discipline, results }: Args): void {
  const doc = initPdf('portrait');
  const dateLoc = `${dayjs(competition.competitionDate).format('D. M. YYYY')}${competition.location ? ', ' + competition.location : ''}`;
  const headerEnd = drawHeader(doc, {
    title: discipline.name,
    competitionName: competition.name,
    competitionDateLocation: dateLoc,
    categoryLabel: category.name,
  });

  const best = bestPerOwner(results);
  const sorted = [...best].sort((a, b) => {
    if (a.rank == null && b.rank == null) return 0;
    if (a.rank == null) return 1;
    if (b.rank == null) return -1;
    return a.rank - b.rank;
  });

  const head = [['Pořadí', 'Start. č.', 'Družstvo', 'Pokus', 'Výsl. čas', 'Ukončen']];
  const body = sorted.map((r) => [
    r.rank != null ? String(r.rank) : '—',
    r.teamStartNumber != null ? String(r.teamStartNumber) : '—',
    r.teamName,
    r.attemptNumber != null ? `${r.attemptNumber}.` : '—',
    formatTime(r.timeFinalMs, r.isNp),
    r.completedAt ? dayjs(r.completedAt).format('HH:mm:ss') : '—',
  ]);

  autoTable(doc, {
    startY: headerEnd,
    margin: { left: PAGE.margin, right: PAGE.margin, bottom: 35 },
    head,
    body,
    styles: { font: 'DejaVu', fontSize: 10, cellPadding: 2, lineColor: [220, 220, 220] },
    headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: 'bold', halign: 'center' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: {
      0: { halign: 'center', cellWidth: 18, fontStyle: 'bold' },
      1: { halign: 'center', cellWidth: 18 },
      3: { halign: 'center', cellWidth: 18 },
      4: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
      5: { halign: 'center', cellWidth: 28 },
    },
    didDrawPage: () =>
      drawFooter(doc, {
        headJudgeName: competition.headJudgeName,
        eventLeaderName: competition.eventLeaderName,
        commissarName: competition.commissarName,
      }),
  });

  drawPageNumbers(doc);

  const filename = safeFilename(`${competition.shortCode}_${category.name}_${discipline.name}.pdf`);
  doc.save(filename);
}
